import { useState, useEffect, useCallback, useRef } from "react";
import api, { inDemo } from "@/lib/api";

const demoBlock = () => {
  if (inDemo()) { window.dispatchEvent(new CustomEvent("demo-locked")); return true; }
  return false;
};

// Generic collection hook with optimistic CRUD + autosave status
export function useCollection(entity, base = "") {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/items/${entity}`);
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [entity, base]);

  useEffect(() => { load(); }, [load]);

  const flash = () => {
    setStatus("saved");
    setTimeout(() => setStatus("idle"), 1500);
  };

  const create = async (payload) => {
    if (demoBlock()) return;
    setStatus("saving");
    try {
      const { data } = await api.post(`${base}/items/${entity}`, payload);
      setItems((p) => [...p, data.item]);
      flash();
      return data.item;
    } catch { setStatus("error"); }
  };

  const update = async (id, payload) => {
    if (demoBlock()) return;
    setStatus("saving");
    setItems((p) => p.map((it) => (it.id === id ? { ...it, ...payload } : it)));
    try {
      await api.put(`${base}/items/${entity}/${id}`, payload);
      flash();
    } catch { setStatus("error"); }
  };

  const remove = async (id) => {
    if (demoBlock()) return;
    setStatus("saving");
    const prev = items;
    setItems((p) => p.filter((it) => it.id !== id));
    try {
      await api.delete(`${base}/items/${entity}/${id}`);
      flash();
    } catch { setItems(prev); setStatus("error"); }
  };

  return { items, setItems, loading, status, load, create, update, remove };
}

// Debounced field autosave hook
export function useDebouncedSave(saveFn, delay = 700) {
  const timer = useRef();
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => saveFn(...args), delay);
  }, [saveFn, delay]);
}
